/**
 * Registro e carregamento de plugins.
 *
 * Tres decisoes que definem o comportamento do arranque:
 *
 *  1. O manifesto e validado ANTES de o plugin correr. Um manifesto errado
 *     (namespace de entidade alheio, escopo de outro plugin) e recusado e dito
 *     em portugues — nao vale a pena descobrir isso a meio de uma escrita.
 *
 *  2. As dependencias sao resolvidas por ordenacao topologica, e um ciclo e
 *     erro fatal com o caminho do ciclo impresso. Ciclos entre plugins nao tem
 *     solucao automatica: alguem tem de partir a dependencia.
 *
 *  3. Falha suave. Um plugin cujo `register()` quebra fica marcado como
 *     `failed` e o aplicativo continua a arrancar sem ele. As financas partidas
 *     nunca podem impedir a mae de ver os lembretes da medicacao.
 */
import {
  validateManifest,
  type EntityDescriptor,
  type FlosPlugin,
  type IntentDescriptor,
  type NotifierLike,
  type PluginContext,
  type PluginManifest,
  type PluginRoute,
  type RepositoryLike,
  type Scope,
  type SessionInfo,
  type WidgetDescriptor,
} from '@flos/shared';
import type { SchemaRegistry } from './db';
import { CoreLocalEvents, type EventBus, type PluginFailurePayload } from './events';
import type { FlosLogger } from './logger';
import type { RepositoryFactory, ValidatorRegistry } from './repository';
import type { SessionService } from './session';

/**
 * Contexto que o nucleo entrega. Estende o contrato partilhado com o registro de
 * validadores — sem isto um plugin nao consegue cumprir a regra 4, porque o
 * `PluginContext` nao previu onde registrar um `Validator`.
 */
export interface FlosPluginContext extends PluginContext {
  validators: ValidatorRegistry;
}

export type PluginState =
  /** Aceite, ainda nao arrancou. */
  | 'pending'
  /** `register()` correu bem. */
  | 'loaded'
  /** Manifesto invalido — nunca chega a correr. */
  | 'rejected'
  /** `register()` lancou. */
  | 'failed'
  /** Uma dependencia nao existe ou falhou. */
  | 'skipped';

export interface PluginEntry {
  plugin: FlosPlugin;
  manifest: PluginManifest;
  state: PluginState;
  /** Motivos, em portugues, para `rejected`/`failed`/`skipped`. */
  problems: string[];
  error?: unknown;
  context?: FlosPluginContext;
  activated?: boolean;
}

export interface BootReport {
  loaded: string[];
  failed: string[];
  skipped: string[];
  rejected: string[];
  order: string[];
}

export interface RegisteredRoute extends PluginRoute {
  pluginId: string;
  /** Caminho absoluto no aplicativo, ex. '/financas/transacao/:id'. */
  fullPath: string;
}

export interface RegisteredWidget extends WidgetDescriptor {
  pluginId: string;
}

export interface RegisteredIntent extends IntentDescriptor {
  pluginId: string;
}

export interface RegisteredEntity extends EntityDescriptor {
  pluginId: string;
}

export class DependencyCycleError extends Error {
  readonly code = 'PLUGIN_DEPENDENCY_CYCLE';
  constructor(readonly cycle: string[]) {
    super(`Ciclo de dependencias entre plugins: ${cycle.join(' -> ')}`);
    this.name = 'DependencyCycleError';
  }
}

/** Servicos publicados por plugins, consumidos por id (regra 8). */
export class ServiceRegistry {
  readonly #services = new Map<string, unknown>();
  readonly #owners = new Map<string, string>();

  provide<T>(serviceId: string, impl: T, owner = 'desconhecido'): void {
    const previous = this.#owners.get(serviceId);
    if (previous && previous !== owner) {
      throw new Error(
        `O servico "${serviceId}" ja foi publicado pelo plugin "${previous}". Escolha outro id.`,
      );
    }
    this.#services.set(serviceId, impl);
    this.#owners.set(serviceId, owner);
  }

  consume<T>(serviceId: string): T | null {
    const found = this.#services.get(serviceId);
    return found === undefined ? null : (found as T);
  }

  ids(): string[] {
    return [...this.#services.keys()].sort();
  }
}

export interface PluginRegistryDeps {
  logger: FlosLogger;
  events: EventBus;
  schema: SchemaRegistry;
  session: SessionService;
  /** Ligados tarde: so existem depois de a base de dados abrir. */
  repositories: () => RepositoryFactory;
  settings: () => { namespace(ns: string): PluginContext['settings'] };
  notifier: () => NotifierLike;
}

export class PluginRegistry {
  readonly #deps: PluginRegistryDeps;
  readonly #entries = new Map<string, PluginEntry>();
  /** Recusados antes de entrarem no mapa (ids repetidos). */
  readonly #duplicates: string[] = [];
  readonly #services = new ServiceRegistry();
  #started = false;

  constructor(deps: PluginRegistryDeps) {
    this.#deps = deps;
  }

  get services(): ServiceRegistry {
    return this.#services;
  }

  /**
   * Aceita um plugin. Devolve `false` (sem lancar) quando o manifesto tem
   * problemas: um plugin mal declarado nao pode derrubar o arranque dos outros.
   */
  register(plugin: FlosPlugin): boolean {
    const manifest = plugin.manifest;
    const logger = this.#deps.logger;

    if (this.#entries.has(manifest.id)) {
      // Nao substituimos o que ja esta registrado: o primeiro a chegar ganha, e
      // o segundo e recusado. Substituir apagaria um plugin que ja funcionava.
      const reason = `ja existe um plugin com o id "${manifest.id}"`;
      logger.error(`Plugin "${manifest.id}" recusado: ${reason}`, { plugin: manifest.id });
      this.#duplicates.push(manifest.id);
      this.#announceFailure(manifest.id, reason);
      return false;
    }

    const problems = validateManifest(manifest);
    if (problems.length > 0) {
      logger.error(`Plugin "${manifest.id}" recusado: manifesto invalido`, {
        plugin: manifest.id,
        problemas: problems,
      });
      for (const problem of problems) {
        logger.error(`  - ${problem}`, { plugin: manifest.id });
      }
      this.#entries.set(manifest.id, {
        plugin,
        manifest,
        state: 'rejected',
        problems,
      });
      this.#announceFailure(manifest.id, problems.join('; '));
      return false;
    }

    this.#entries.set(manifest.id, { plugin, manifest, state: 'pending', problems: [] });

    // As entidades tem de estar no registro de esquema ANTES de a base de dados
    // abrir — o IndexedDB nao aceita tabelas novas com a base de dados aberta.
    try {
      this.#deps.schema.register(manifest.entities, manifest.id);
    } catch (error) {
      const reason = error instanceof Error ? error.message : String(error);
      logger.error(`Plugin "${manifest.id}" recusado: ${reason}`, { plugin: manifest.id });
      this.#entries.set(manifest.id, {
        plugin,
        manifest,
        state: 'rejected',
        problems: [reason],
        error,
      });
      this.#announceFailure(manifest.id, reason);
      return false;
    }

    // Marcar as entidades sensiveis no logger: a partir daqui, nada do conteudo
    // delas entra no diagnostico (ADR-0006).
    logger.markSensitive(
      manifest.entities.filter((e) => e.sensitivity === 'high').map((e) => e.name),
    );
    return true;
  }

  registerAll(plugins: readonly FlosPlugin[]): void {
    for (const plugin of plugins) this.register(plugin);
  }

  /** Arranca todos os plugins aceites, pela ordem das dependencias. */
  async start(): Promise<BootReport> {
    if (this.#started) {
      throw new Error('Os plugins ja foram arrancados nesta sessao.');
    }

    const candidates = [...this.#entries.values()].filter((e) => e.state === 'pending');
    // A ordenacao vem antes de marcar como arrancado: um ciclo e um erro de
    // desenho que deve poder ser corrigido e tentado outra vez sem reiniciar.
    const order = topologicalOrder(candidates.map((e) => e.manifest));
    this.#started = true;

    const report: BootReport = {
      loaded: [],
      failed: [],
      skipped: [],
      rejected: [
        ...[...this.#entries.values()]
          .filter((e) => e.state === 'rejected')
          .map((e) => e.manifest.id),
        ...this.#duplicates,
      ],
      order,
    };

    for (const id of order) {
      const entry = this.#entries.get(id);
      if (!entry || entry.state !== 'pending') continue;

      const blocking = this.#unmetDependencies(entry.manifest);
      if (blocking.length > 0) {
        entry.state = 'skipped';
        entry.problems = blocking;
        report.skipped.push(id);
        this.#deps.logger.warn(
          `Plugin "${id}" ignorado: depende de ${blocking.join(', ')} que nao arrancou`,
          { plugin: id },
        );
        this.#announceFailure(id, `dependencias em falta: ${blocking.join(', ')}`);
        continue;
      }

      const context = this.#buildContext(entry.manifest);
      entry.context = context;
      try {
        await entry.plugin.register(context);
        entry.state = 'loaded';
        report.loaded.push(id);
        this.#deps.logger.info(`Plugin "${id}" pronto`, {
          plugin: id,
          version: entry.manifest.version,
        });
      } catch (error) {
        // Falha suave: um plugin partido nao pode levar o aplicativo com ele.
        const reason = error instanceof Error ? error.message : String(error);
        entry.state = 'failed';
        entry.problems = [reason];
        entry.error = error;
        report.failed.push(id);
        this.#deps.logger.error(`Plugin "${id}" falhou o arranque e foi desligado`, {
          plugin: id,
          erro: reason,
        });
        this.#announceFailure(id, reason);
      }
    }

    this.#deps.events.emit(CoreLocalEvents.PLUGINS_STARTED, report);
    return report;
  }

  /** Chamado quando o usuário abre o plugin pela primeira vez na sessao. */
  async activate(pluginId: string): Promise<void> {
    const entry = this.#entries.get(pluginId);
    if (!entry || entry.state !== 'loaded' || entry.activated) return;
    entry.activated = true;
    if (!entry.plugin.activate || !entry.context) return;
    try {
      await entry.plugin.activate(entry.context);
    } catch (error) {
      const reason = error instanceof Error ? error.message : String(error);
      this.#deps.logger.error(`Plugin "${pluginId}" falhou ao abrir`, {
        plugin: pluginId,
        erro: reason,
      });
    }
  }

  async deactivate(pluginId: string): Promise<void> {
    const entry = this.#entries.get(pluginId);
    if (!entry || !entry.plugin.deactivate || !entry.context) return;
    try {
      await entry.plugin.deactivate(entry.context);
      entry.activated = false;
    } catch (error) {
      const reason = error instanceof Error ? error.message : String(error);
      this.#deps.logger.error(`Plugin "${pluginId}" falhou ao desligar`, {
        plugin: pluginId,
        erro: reason,
      });
    }
  }

  // --- Consulta ---

  entry(pluginId: string): PluginEntry | undefined {
    return this.#entries.get(pluginId);
  }

  state(pluginId: string): PluginState | 'unknown' {
    return this.#entries.get(pluginId)?.state ?? 'unknown';
  }

  all(): PluginEntry[] {
    return [...this.#entries.values()];
  }

  /** Manifestos dos plugins que arrancaram, visiveis para o usuário ativo. */
  manifests(): PluginManifest[] {
    return this.#loaded()
      .filter((entry) => this.#isVisible(entry.manifest))
      .map((entry) => entry.manifest);
  }

  routes(): RegisteredRoute[] {
    const out: RegisteredRoute[] = [];
    for (const entry of this.#loaded()) {
      if (!this.#isVisible(entry.manifest)) continue;
      for (const route of entry.manifest.routes) {
        if (!this.#allows(route.requires)) continue;
        out.push({
          ...route,
          pluginId: entry.manifest.id,
          fullPath: joinPath(entry.manifest.id, route.path),
        });
      }
    }
    return out;
  }

  widgets(): RegisteredWidget[] {
    const out: RegisteredWidget[] = [];
    for (const entry of this.#loaded()) {
      if (!this.#isVisible(entry.manifest)) continue;
      for (const widget of entry.manifest.widgets ?? []) {
        if (!this.#allows(widget.requires)) continue;
        out.push({ ...widget, pluginId: entry.manifest.id });
      }
    }
    return out.sort((a, b) => a.order - b.order);
  }

  intents(): RegisteredIntent[] {
    const out: RegisteredIntent[] = [];
    for (const entry of this.#loaded()) {
      if (!this.#isVisible(entry.manifest)) continue;
      for (const intent of entry.manifest.intents ?? []) {
        if (!this.#allows(intent.requires)) continue;
        out.push({ ...intent, pluginId: entry.manifest.id });
      }
    }
    return out;
  }

  /** Entidades visiveis ao usuário ativo. Para o esquema use `allEntities()`. */
  entities(): RegisteredEntity[] {
    const out: RegisteredEntity[] = [];
    for (const entry of this.#loaded()) {
      if (!this.#isVisible(entry.manifest)) continue;
      for (const entity of entry.manifest.entities) {
        out.push({ ...entity, pluginId: entry.manifest.id });
      }
    }
    return out;
  }

  /** Todas as entidades declaradas, sem filtro de permissoes. */
  allEntities(): RegisteredEntity[] {
    const out: RegisteredEntity[] = [];
    for (const entry of this.#entries.values()) {
      if (entry.state === 'rejected') continue;
      for (const entity of entry.manifest.entities) {
        out.push({ ...entity, pluginId: entry.manifest.id });
      }
    }
    return out;
  }

  // --- Interno ---

  #loaded(): PluginEntry[] {
    return [...this.#entries.values()].filter((entry) => entry.state === 'loaded');
  }

  /** Sem `<plugin>:read` o plugin nem aparece na navegacao. */
  #isVisible(manifest: PluginManifest): boolean {
    return this.#allows(`${manifest.id}:read`);
  }

  #allows(scope: Scope | undefined): boolean {
    if (!scope) return true;
    if (!this.#deps.session.isActive()) return false;
    return this.#deps.session.can(scope);
  }

  #unmetDependencies(manifest: PluginManifest): string[] {
    const missing: string[] = [];
    for (const dependency of manifest.dependencies) {
      if (this.#entries.get(dependency)?.state !== 'loaded') missing.push(dependency);
    }
    return missing;
  }

  #announceFailure(pluginId: string, reason: string): void {
    const payload: PluginFailurePayload = { pluginId, reason };
    this.#deps.events.emit(CoreLocalEvents.PLUGIN_FAILED, payload);
  }

  #buildContext(manifest: PluginManifest): FlosPluginContext {
    const deps = this.#deps;
    const logger = deps.logger.child({ plugin: manifest.id });
    const repositories = deps.repositories();

    return {
      manifest,
      logger,
      events: deps.events,
      repository: <T>(entity: string): RepositoryLike<T> => {
        if (!deps.schema.has(entity)) {
          throw new Error(
            `O plugin "${manifest.id}" pediu a entidade "${entity}", que nao esta declarada em nenhum manifesto.`,
          );
        }
        if (!entity.startsWith(`${manifest.id}.`)) {
          // Ler dados de outro plugin e legitimo (um cartao de saude pode
          // mostrar o nome do membro); escrever nao. Fica registrado para
          // aparecer em revisao se alguem abusar.
          logger.debug('Repositorio de entidade de outro plugin', { entity });
        }
        return repositories.for(entity) as unknown as RepositoryLike<T>;
      },
      provide: <T>(serviceId: string, impl: T) =>
        this.#services.provide(serviceId, impl, manifest.id),
      consume: <T>(serviceId: string) => this.#services.consume<T>(serviceId),
      can: (scope: Scope) => deps.session.can(scope),
      settings: deps.settings().namespace(manifest.id),
      notify: deps.notifier(),
      session: (): SessionInfo => deps.session.current(),
      validators: repositories.validators,
    };
  }
}

/**
 * Ordenacao topologica com detecao de ciclo. Percorremos em profundidade e
 * guardamos a pilha, para poder mostrar o caminho exato do ciclo — dizer so
 * "ha um ciclo" obriga quem depura a descobri-lo a mao.
 */
export function topologicalOrder(manifests: readonly PluginManifest[]): string[] {
  const byId = new Map<string, PluginManifest>();
  for (const manifest of manifests) byId.set(manifest.id, manifest);

  const visited = new Set<string>();
  const stack: string[] = [];
  const inStack = new Set<string>();
  const order: string[] = [];

  const visit = (id: string): void => {
    if (visited.has(id)) return;
    if (inStack.has(id)) {
      const from = stack.indexOf(id);
      throw new DependencyCycleError([...stack.slice(from === -1 ? 0 : from), id]);
    }
    const manifest = byId.get(id);
    // Dependencia inexistente nao e ciclo: e tratada como falha suave no arranque.
    if (!manifest) return;

    inStack.add(id);
    stack.push(id);
    for (const dependency of manifest.dependencies) visit(dependency);
    stack.pop();
    inStack.delete(id);
    visited.add(id);
    order.push(id);
  };

  for (const manifest of manifests) visit(manifest.id);
  return order;
}

function joinPath(pluginId: string, path: string): string {
  const suffix = path === '/' || path === '' ? '' : path.startsWith('/') ? path : `/${path}`;
  return `/${pluginId}${suffix}`;
}

/** Notificador de reserva enquanto `@flos/notifications` nao esta ligado. */
export function createFallbackNotifier(logger: FlosLogger): NotifierLike {
  return {
    async local(input) {
      logger.warn('Notificacao local pedida sem servico de notificacoes ligado', {
        titulo: input.title,
      });
    },
    async family(input) {
      logger.warn('Notificacao a familia pedida sem servico de notificacoes ligado', {
        titulo: input.title,
      });
    },
  };
}
